import React from 'react';
import { FolderInput, Brain, GitBranch, Layers } from 'lucide-react';

const STEPS = [
  {
    icon: FolderInput,
    title: 'Drop a project folder',
    body:
      'Point Sortio at a project root: drawings, BOQs, MOMs, RFIs, change request forms, contractor submittals, AutoCAD plot logs. Sortio scans the entire archive locally on your Mac, including bilingual Arabic and English filenames.',
  },
  {
    icon: Brain,
    title: 'AI reads each drawing',
    body:
      "Sortio extracts discipline codes (AR, ST, PW, TR), drawing numbers, revision tags (V1, V2, V3), CRF numbers, project phases, and client or contractor references from each file. The read happens on your Mac. By default Sortio uses our cloud (OpenAI) to plan the sort and sends filenames and the extracted entities, with file contents sent only if you enable a content-based feature. Run a local LLM (Ollama) or BYOK and nothing is uploaded to our servers.",
  },
  {
    icon: GitBranch,
    title: 'Files routed by discipline',
    body:
      'Architectural drawings land in AR. Structural drawings in ST. Plumbing in PW. Telecom in TR. Change requests go to a CRF subfolder grouped by request number. BOQs and MOMs file by project and date. You preview every move with confidence scores before anything is touched.',
  },
  {
    icon: Layers,
    title: 'Revisions stay versioned',
    body:
      "Sortio detects V1, V2, V3 in filenames and keeps every revision in a versioned subfolder, so a new revision never silently overwrites the prior one. DWG and PDF pairs stay together. The original folder is backed up before changes apply, so you can undo any run.",
  },
];

const Workflow = () => {
  return (
    <section id="workflow" className="my-20">
      <div className="text-center mb-14">
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          How it works
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">
          Four steps from a 7,000 file project archive to clean, discipline organized folders.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <div
              key={i}
              className="relative bg-apple-800/40 backdrop-blur-sm rounded-2xl border border-apple-700/50 p-6 flex flex-col"
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-sortio-primary/15 border border-sortio-primary/30 flex items-center justify-center mr-3">
                  <Icon className="text-sortio-primary" size={18} />
                </div>
                <span className="text-xs font-medium uppercase tracking-wider text-apple-400">
                  Step {i + 1}
                </span>
              </div>
              <h3 className="text-lg font-medium text-apple-50 mb-2">{step.title}</h3>
              <p className="text-sm text-apple-200 leading-relaxed">{step.body}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-10 max-w-3xl mx-auto text-center text-apple-300 text-sm leading-relaxed">
        <p>
          Sortio is filesystem transparent. It works with whatever AutoCAD, BIM, Revit, or PDF
          workflow you already have, because it reads and writes plain files in your existing
          folder structure. No new repository to migrate to, no plugin to install in your CAD
          software.
        </p>
      </div>
    </section>
  );
};

export default Workflow;
